import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { quizService } from '../services/apiService'

const Quizzes = () => {
  const [quizzes, setQuizzes] = useState([])
  const [attempts, setAttempts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [quizzesRes, attemptsRes] = await Promise.all([
        quizService.getQuizzes(),
        quizService.getMyAttempts(),
      ])

      const quizzesData = quizzesRes.data.results || quizzesRes.data
      const attemptsData = attemptsRes.data.results || attemptsRes.data

      setQuizzes(Array.isArray(quizzesData) ? quizzesData : [])
      setAttempts(Array.isArray(attemptsData) ? attemptsData : [])
    } catch (error) {
      console.error('Error fetching quizzes:', error)
    } finally {
      setLoading(false)
    }
  }

  const getQuizAttempts = (quizId) => {
    return attempts.filter((attempt) => attempt.quiz === quizId)
  }

  const getBestScore = (quizId) => {
    const quizAttempts = getQuizAttempts(quizId)
    if (quizAttempts.length === 0) return null
    return Math.max(...quizAttempts.map((a) => a.score || 0))
  }

  const getLatestAttempt = (quizId) => {
    const quizAttempts = getQuizAttempts(quizId)
    if (quizAttempts.length === 0) return null
    return quizAttempts.sort(
      (a, b) => new Date(b.started_at) - new Date(a.started_at),
    )[0]
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    )
  }

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <div className='mb-8'>
        <h1 className='text-3xl font-bold text-gray-900'>My Quizzes</h1>
        <p className='text-gray-600 mt-2'>
          Take quizzes from your enrolled courses
        </p>
      </div>

      {quizzes.length > 0 ? (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {quizzes.map((quiz) => {
            const bestScore = getBestScore(quiz.id)
            const latestAttempt = getLatestAttempt(quiz.id)
            const hasIncompleteAttempt =
              latestAttempt && !latestAttempt.submitted_at

            return (
              <div
                key={quiz.id}
                className='bg-white rounded-lg shadow hover:shadow-lg transition-shadow'
              >
                <div className='p-6'>
                  <div className='flex items-start justify-between mb-4'>
                    <h3 className='text-lg font-semibold text-gray-900 flex-1'>
                      {quiz.title}
                    </h3>
                    {bestScore !== null && (
                      <span
                        className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                          bestScore >= quiz.passing_score
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        Best: {bestScore.toFixed(0)}%
                      </span>
                    )}
                  </div>

                  <p className='text-sm text-gray-600 mb-4 line-clamp-3'>
                    {quiz.description}
                  </p>

                  <div className='space-y-2 text-sm text-gray-600 mb-4'>
                    <div className='flex items-center'>
                      <svg
                        className='h-4 w-4 mr-2'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                        />
                      </svg>
                      {quiz.time_limit} minutes
                    </div>
                    <div className='flex items-center'>
                      <svg
                        className='h-4 w-4 mr-2'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                        />
                      </svg>
                      Passing: {quiz.passing_score}%
                    </div>
                    <div className='flex items-center'>
                      <svg
                        className='h-4 w-4 mr-2'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                        />
                      </svg>
                      {quiz.total_questions || 0} questions
                    </div>
                  </div>

                  {getQuizAttempts(quiz.id).length > 0 && (
                    <div className='text-sm text-gray-500 mb-4'>
                      Attempts: {getQuizAttempts(quiz.id).length}
                    </div>
                  )}

                  <Link
                    to={`/quizzes/${quiz.id}/take`}
                    className={`block w-full text-center px-4 py-2 rounded-md font-medium ${
                      hasIncompleteAttempt
                        ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                        : 'bg-primary-600 hover:bg-primary-700 text-white'
                    }`}
                  >
                    {hasIncompleteAttempt ? 'Continue Quiz' : 'Start Quiz'}
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className='text-center py-12 bg-white rounded-lg shadow'>
          <svg
            className='mx-auto h-12 w-12 text-gray-400 mb-4'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'
            />
          </svg>
          <p className='text-gray-500 mb-2'>No quizzes available yet</p>
          <p className='text-sm text-gray-400'>
            Quizzes from your enrolled courses will appear here
          </p>
        </div>
      )}
    </div>
  )
}

export default Quizzes
